import React, { useEffect, lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Analytics } from '@vercel/analytics/react';
import MatterportViewer from "./components/MatterportViewer";
import Chat from "./components/Chat";
import { LoadingProvider, useLoading } from "./context/LoadingContext";
import { ChatProvider } from './context/ChatContext';
import { LanguageProvider } from './context/LanguageContext';
import { CaptionsProvider } from './context/CaptionsContext';
import CaptionsButton from './components/Captions/CaptionsButton';
import CaptionStrip from './components/Captions/CaptionStrip';
import TranscriptDrawer from './components/Captions/TranscriptDrawer';
import { PROJECT_CONFIG } from './config/projectConfig';
import ErrorBoundary from './components/ErrorBoundary';
import CameraPoseDebug from './components/CameraPoseDebug';
import { installEditModeShortcut } from './utils/editMode';

// Lazy-load View360Selector — Pannellum is heavy (~200KB + WebGL context).
// Only parsed/initialised when the component is first rendered.
const View360Selector = lazy(() => import('./components/View360Selector'));

const AppContent = () => {
  const { isLoaded } = useLoading();

  // Install the master Alt+Shift+E shortcut that unlocks all editor panels.
  useEffect(() => { installEditModeShortcut(); }, []);

  // Idle auto-refresh: kiosk-friendly safeguard. After 2 hours of no
  // user interaction the page reloads itself, returning the app to its
  // initial state. While the live stream is playing the timer is
  // paused (LinksPanel emits `liveStreamActive` events for this).
  useEffect(() => {
    const IDLE_MS = 2 * 60 * 60 * 1000; // 2 hours
    let timer = null;
    let liveActive = false;

    const reset = () => {
      if (timer) clearTimeout(timer);
      if (liveActive) return;
      timer = setTimeout(() => {
        try { window.location.reload(); } catch { /* noop */ }
      }, IDLE_MS);
    };

    const onLive = (e) => {
      liveActive = !!e.detail;
      reset();
    };

    const events = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'];
    events.forEach((evt) => window.addEventListener(evt, reset, { passive: true }));
    window.addEventListener('liveStreamActive', onLive);
    reset();

    return () => {
      if (timer) clearTimeout(timer);
      events.forEach((evt) => window.removeEventListener(evt, reset));
      window.removeEventListener('liveStreamActive', onLive);
    };
  }, []);
  return (
    <>
      <Routes>
        {/* Main app route */}
        <Route
          path="/"
          element={
            <div className="relative w-full h-screen overflow-hidden" style={{ minHeight: '100vh' }}>
              <div className="w-full h-full" style={{ width: '100%', height: '100%' }}>
                <MatterportViewer />
              </div>
              {isLoaded && PROJECT_CONFIG.features.chatEnabled && <Chat />}
              {isLoaded && <CameraPoseDebug />}
              {isLoaded && PROJECT_CONFIG.features.readAlongCaptionsEnabled && <CaptionsButton />}
              {isLoaded && PROJECT_CONFIG.features.readAlongCaptionsEnabled && <CaptionStrip />}
              {isLoaded && PROJECT_CONFIG.features.readAlongCaptionsEnabled && <TranscriptDrawer />}
            </div>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
};

function App() {
  return (
    <ErrorBoundary>
    <LanguageProvider>
    <CaptionsProvider>
    <ChatProvider>
      <LoadingProvider>
        <Suspense fallback={null}>
          <View360Selector />
        </Suspense>
        <AppContent />
        <Analytics />
      </LoadingProvider>
    </ChatProvider>
    </CaptionsProvider>
    </LanguageProvider>
    </ErrorBoundary>
  );
}

export default App;